/**
 * Character Select — "CHOOSE YOUR HEAD!" / "PICK YOUR HEAD" (design 05 · UI — Character Select, E8s1I;
 * design/exports/select.png): red/blue split hype rays, a 5×2 grid of head cards (the 9-character roster + a
 * GET COINS tile) with 1P/2P cursors, big portraits with gloves on each side, name plates with stat pips and the
 * signature special, RANDOM HEAD, READY! stamps. Locked (unowned) heads show a lock badge + coin price
 * (handegg-roster.md "Pricing"); picking one spends coins through the local wallet (`../wallet.ts`) before the
 * normal pick flow continues. The wallet balance (+ GET COINS shortcut to the Store screen) sits top-right.
 *
 * VS CPU: P1 picks, then picks the CPU's head, then the CPU difficulty. LOCAL VERSUS: both players pick at the
 * same time — P1 on WASD + J (K = undo) or pad 1, P2 on the arrows + Numpad1/. (Numpad2// = undo) or pad 2.
 */
import './character-select.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  catalogFor,
  CHARACTER_IDS,
  CHARACTERS,
  characterById,
  DIFFICULTY_NAMES,
  randomSeed,
  specialInfo,
  type CharacterId,
  type Difficulty,
} from '@hf/shared';
import { DEVICE_ID, type Bindings, type InputManager, type MenuAction } from '@hf/client-core/input/types';
import { useActions, useServices } from '../actions';
import { preloadImages } from '../assets';
import {
  CoinBalance,
  Glove,
  Head,
  Hints,
  LockBadge,
  Pips,
  preloadKeysFor,
  PriceChip,
  Rays,
  SpecialIcon,
} from '../components';
import {
  buildOfflineSetup,
  DIFFICULTIES,
  draft,
  pendingMatch,
  randomCharacter,
  updateDraft,
} from '@hf/client-core/ui-model/flow';
import { inputStyle, useMenuLayer, type MenuSource } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import { venueChyron } from '@hf/client-core/ui-model/format';
import { DIFFICULTY_BLURB } from '@hf/client-core/ui-model/screens';
import { unlockSku, wallet, walletBalance } from '../wallet';
import { openStore as openStoreFrom } from './store-nav';
import { PORTAL } from '../../portal';
export { DIFFICULTY_BLURB } from '@hf/client-core/ui-model/screens';

const STAT_ROWS = [
  ['SPEED', 'spd'],
  ['JUMP', 'jmp'],
  ['ARM', 'arm'],
  ['TACKLE', 'tkl'],
  ['TOSS', 'toss'],
] as const;

type Phase = 'pick' | 'cpu' | 'difficulty' | 'done';

/**
 * The grid (design/exports/select.png): 5 columns × 2 rows — the 9-character roster (`CHARACTER_IDS` order, cells
 * 0..8) plus a GET COINS tile (cell 9, `GET_COINS_CELL`) that jumps to the Store instead of picking a head.
 * RANDOM HEAD stays its own full-width bar below the grid (`RANDOM_CELL`, outside the 0..9 cell range), as before.
 */
export const CELL_CHARACTERS: readonly CharacterId[] = CHARACTER_IDS;
const GRID_COLS = 5;
export const GET_COINS_CELL = CELL_CHARACTERS.length; // 9
export const RANDOM_CELL = GET_COINS_CELL + 1; // 10

/** Cursor movement on the 5×2 grid (+ GET COINS tile) + the RANDOM HEAD bar below it. */
export const moveCursor = (cur: number, dir: 'up' | 'down' | 'left' | 'right'): number => {
  if (cur === RANDOM_CELL) return dir === 'up' ? GRID_COLS : cur;
  const col = cur % GRID_COLS;
  if (dir === 'left') return col === 0 ? cur : cur - 1;
  if (dir === 'right') return col === GRID_COLS - 1 || cur + 1 > GET_COINS_CELL ? cur : cur + 1;
  if (dir === 'up') return cur < GRID_COLS ? cur : cur - GRID_COLS;
  const next = cur + GRID_COLS;
  return next <= GET_COINS_CELL ? next : RANDOM_CELL;
};

const cellOf = (c: CharacterId): number => Math.max(0, CELL_CHARACTERS.indexOf(c));

/** Map a physical key to a menu action through a keyboard layout's bindings (local 2P). */
export const keyToAction = (b: Bindings, code: string): MenuAction | 'undo' | null => {
  if (b.L.includes(code)) return 'left';
  if (b.R.includes(code)) return 'right';
  if (b.U.includes(code)) return 'up';
  if (b.D.includes(code)) return 'down';
  if (b.A.includes(code)) return 'confirm';
  if (b.S.includes(code)) return 'undo';
  return null;
};

/**
 * LOCAL VERSUS button hints, built from the CURRENT bindings of each keyboard half (they follow rebinding):
 * "P1: WASD · J  PICK", "K  UNDO", "P2: ↑←↓→ · NUM 1  PICK", "NUM 2  UNDO". On pads: "A PICK · B UNDO / BACK".
 */
export const localPickHints = (
  input: Pick<InputManager, 'bindingsFor' | 'labelFor'>,
  style: 'kb' | 'pad' | 'touch',
): [string, string][] => {
  if (style === 'pad')
    return [
      ['up|down|left|right', 'MOVE'],
      ['confirm', 'PICK'],
      ['back', 'UNDO / BACK'],
    ];
  const label = (id: string, code: string | undefined): string => (code ? input.labelFor(id, code) : '—');
  const side = (n: 1 | 2): [[string, string], [string, string]] => {
    const id = n === 1 ? DEVICE_ID.KB_P1 : DEVICE_ID.KB_P2;
    const b = input.bindingsFor(id);
    const dirs = [b.U[0], b.L[0], b.D[0], b.R[0]].map((c) => label(id, c));
    const move = dirs.every((d) => [...d].length === 1) ? dirs.join('') : dirs.join(' ');
    return [
      [`P${n}: ${move} · ${label(id, b.A[0])}`, 'PICK'],
      [label(id, b.S[0]), 'UNDO'],
    ];
  };
  return [...side(1), ...side(2), ['back', 'BACK']];
};

export function CharacterSelect() {
  const actions = useActions();
  const { input, audio } = useServices();
  const d = draft.value;
  const local = d.mode === 'local';
  const [cursor, setCursor] = useState<[number, number]>(() => [
    cellOf(d.characters[0]),
    cellOf(d.characters[1]),
  ]);
  const [picked, setPickedState] = useState<[CharacterId | null, CharacterId | null]>([null, null]);
  const [phase, setPhaseState] = useState<Phase>('pick');
  // Refs mirror the state synchronously: two players can press in the same frame, before a re-render.
  const pickedRef = useRef(picked);
  const phaseRef = useRef(phase);
  const setPicked = (v: [CharacterId | null, CharacterId | null]): void => {
    pickedRef.current = v;
    setPickedState(v);
  };
  const setPhase = (v: Phase): void => {
    phaseRef.current = v;
    setPhaseState(v);
  };
  const [difficulty, setDifficultyState] = useState<Difficulty>(d.difficulty);
  const difficultyRef = useRef(difficulty);
  const setDifficulty = (f: Difficulty | ((v: Difficulty) => Difficulty)): void => {
    difficultyRef.current = typeof f === 'function' ? f(difficultyRef.current) : f;
    setDifficultyState(difficultyRef.current);
  };
  const [flash, setFlash] = useState(0);
  const leaving = useRef(false);

  useEffect(() => preloadImages(preloadKeysFor()), []);

  const charAt = (cell: number, fallback: CharacterId): CharacterId =>
    cell === RANDOM_CELL ? fallback : (CELL_CHARACTERS[cell] ?? fallback);
  /** Which side the CPU-mode player is driving right now. */
  const drivingSide = (): 0 | 1 =>
    !local && (phaseRef.current === 'cpu' || phaseRef.current === 'difficulty') ? 1 : 0;

  const finish = (chars: [CharacterId, CharacterId], diff: Difficulty): void => {
    if (leaving.current) return;
    leaving.current = true;
    setPhase('done');
    playSfx(audio, UI_SFX.start);
    updateDraft({ characters: chars, difficulty: diff });
    const next = { ...draft.peek(), characters: chars, difficulty: diff };
    const seed = randomSeed();
    const setup = buildOfflineSetup(next, seed, ui.settings.peek().playerName);
    const p1 = ui.settings.peek().playerName.trim().toUpperCase() || 'PLAYER 1';
    pendingMatch.value = {
      setup,
      venue: venueChyron(setup.config, local ? 'LOCAL VERSUS' : 'EXHIBITION'),
      tags: [`P1 · ${p1}`, local ? 'P2 · COUCH RIVAL' : `CPU · ${DIFFICULTY_NAMES[diff]}`],
    };
    setTimeout(() => actions.goto('versus'), 650);
  };

  /** GET COINS isn't a head: jump to the Store instead of picking (design/exports/select.png). */
  const openStore = (): void => {
    playSfx(audio, UI_SFX.confirm);
    openStoreFrom((s) => actions.goto(s), 'character_select', 'heads');
  };

  const lock = (side: 0 | 1, cell: number): void => {
    if (cell === GET_COINS_CELL) {
      if (PORTAL) return;
      openStore();
      return;
    }
    let ch = cell === RANDOM_CELL ? randomCharacter() : charAt(cell, 'rookie');
    if (cell === RANDOM_CELL) {
      // RANDOM HEAD never force-spends coins on a locked head: re-roll among owned heads (always ≥ the 4 free ones).
      for (let tries = 0; !wallet.owns(ch) && tries < 20; tries++) ch = randomCharacter();
      if (!wallet.owns(ch)) ch = 'rookie';
      setCursor((c) => (side === 0 ? [cellOf(ch), c[1]] : [c[0], cellOf(ch)]));
    } else if (!wallet.owns(ch)) {
      // handegg-roster.md "Pricing": spend coins to unlock, then continue the pick; denied = stay put.
      if (!unlockSku(ch, catalogFor(ch).priceCoins)) {
        playSfx(audio, UI_SFX.error);
        return;
      }
    }
    playSfx(audio, UI_SFX.slam);
    setFlash((f) => f + 1);
    const cur = pickedRef.current;
    const next: [CharacterId | null, CharacterId | null] = side === 0 ? [ch, cur[1]] : [cur[0], ch];
    setPicked(next);
    if (local) {
      if (next[0] && next[1]) finish([next[0], next[1]], difficultyRef.current);
    } else if (side === 0) setPhase('cpu');
    else setPhase('difficulty');
  };

  const unlock = (side: 0 | 1): boolean => {
    const p = pickedRef.current;
    if (!p[side]) return false;
    playSfx(audio, UI_SFX.back);
    setPicked(side === 0 ? [null, p[1]] : [p[0], null]);
    if (!local) setPhase(side === 0 ? 'pick' : 'cpu');
    return true;
  };

  const act = (side: 0 | 1, a: MenuAction | 'undo'): void => {
    const phase = phaseRef.current;
    const picked = pickedRef.current;
    if (phase === 'done') return;
    if (!local && phase === 'difficulty') {
      if (a === 'left' || a === 'up') {
        setDifficulty((v) => DIFFICULTIES[Math.max(0, DIFFICULTIES.indexOf(v) - 1)]!);
        playSfx(audio, UI_SFX.move);
      } else if (a === 'right' || a === 'down') {
        setDifficulty((v) => DIFFICULTIES[Math.min(DIFFICULTIES.length - 1, DIFFICULTIES.indexOf(v) + 1)]!);
        playSfx(audio, UI_SFX.move);
      } else if (a === 'confirm' || a === 'pause')
        finish([picked[0] ?? 'rookie', picked[1] ?? 'zorg'], difficultyRef.current);
      else if (a === 'back' || a === 'undo') unlock(1);
      return;
    }
    if (a === 'undo') {
      unlock(side);
      return;
    }
    if (a === 'back') {
      // Undo the most recent pick first; with nothing picked, leave.
      if (!local && phase === 'cpu') unlock(0);
      else if (!unlock(side) && !(local && unlock(side === 0 ? 1 : 0))) {
        playSfx(audio, UI_SFX.back);
        actions.goto('mode_select');
      }
      return;
    }
    if (picked[side]) return; // locked in
    if (a === 'confirm' || a === 'pause') lock(side, cursor[side]);
    else {
      setCursor((c) => {
        const n = moveCursor(c[side], a);
        if (n !== c[side]) playSfx(audio, UI_SFX.move);
        return side === 0 ? [n, c[1]] : [c[0], n];
      });
    }
  };

  // Router: pads by index, keyboard (solo layout) and touch drive the current side; in local mode the keyboard
  // halves are read directly below, so the router only takes Escape (back) from keyboards.
  useMenuLayer((a: MenuAction, source: MenuSource) => {
    if (local) {
      if (source.kind === 'pad') {
        const pad =
          source.deviceId === DEVICE_ID.pad(1) ? 1 : source.deviceId === DEVICE_ID.pad(0) ? 0 : null;
        if (pad !== null) act(pad, a);
        return;
      }
      if (source.kind === 'kb') {
        if (a === 'back') act(pickedRef.current[1] ? 1 : 0, 'back');
        return;
      }
      act(pickedRef.current[0] ? 1 : 0, a);
      return;
    }
    act(drivingSide(), a);
  });

  // Local 2P: each keyboard half drives its own cursor.
  useEffect(() => {
    if (!local) return;
    const b1 = input.bindingsFor(DEVICE_ID.KB_P1);
    const b2 = input.bindingsFor(DEVICE_ID.KB_P2);
    const onKey = (e: KeyboardEvent): void => {
      if (e.repeat) return;
      const a1 = keyToAction(b1, e.code);
      const a2 = a1 ? null : keyToAction(b2, e.code);
      if (a1) act(0, a1);
      else if (a2) act(1, a2);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const clickCell = (cell: number): void => {
    const phase = phaseRef.current;
    const picked = pickedRef.current;
    if (phase === 'done') return;
    const side: 0 | 1 = local ? (picked[0] ? 1 : 0) : drivingSide();
    if (!local && phase === 'difficulty') return;
    if (picked[side]) return;
    setCursor((c) => (side === 0 ? [cell, c[1]] : [c[0], cell]));
    lock(side, cell);
  };

  const shown: [CharacterId, CharacterId] = [
    picked[0] ?? charAt(cursor[0], d.characters[0]),
    picked[1] ?? (local || phase !== 'pick' ? charAt(cursor[1], d.characters[1]) : d.characters[1]),
  ];
  const p2Label = local ? '2P' : 'CPU';
  const prompt = local
    ? picked[0] && picked[1]
      ? "LET'S GO!"
      : 'BOTH PLAYERS: PICK YOUR HEAD'
    : phase === 'pick'
      ? 'PICK YOUR HEAD'
      : phase === 'cpu'
        ? 'NOW PICK THE CPU'
        : phase === 'difficulty'
          ? 'HOW TOUGH IS THE CPU?'
          : "LET'S GO!";

  return (
    <div class={`hf-screen hf-select ${phase === 'done' ? 'done' : ''}`} data-screen="character_select">
      <div class="hf-select__bg" aria-hidden="true">
        <Rays a="#e8343c" b="#5a0f1f" rays={20} x="18%" y="45%" speed={70} vignette={false} class="p1" />
        <Rays
          a="#2f7df6"
          b="#0f2a66"
          rays={20}
          x="82%"
          y="45%"
          speed={70}
          reverse
          vignette={false}
          class="p2"
        />
        <div class="hf-select__split" />
        <div class="hf-select__shade" />
      </div>

      <h1 class="hf-select__title t-display">CHOOSE YOUR HEAD!</h1>
      <div class="hf-select__prompt t-broadcast" aria-live="polite" key={prompt}>
        {prompt}
      </div>
      {PORTAL ? null : (
        <CoinBalance balance={walletBalance.value} onGetCoins={openStore} class="hf-select__balance" />
      )}

      {([0, 1] as const).map((side) => (
        <Portrait
          key={side}
          side={side}
          character={shown[side]}
          ready={!!picked[side]}
          tag={side === 0 ? '1P' : p2Label}
          dim={!local && side === 1 && phase === 'pick'}
          flash={flash}
        />
      ))}

      <div class="hf-select__grid" role="listbox" aria-label="Heads">
        {CHARACTERS.map((c, cell) => {
          const on0 = cursor[0] === cell && (phase === 'pick' || local || !!picked[0]);
          const on1 = cursor[1] === cell && (local || phase !== 'pick');
          const owned = wallet.owns(c.id);
          const cat = catalogFor(c.id);
          return (
            <button
              key={c.id}
              role="option"
              aria-selected={on0 || on1}
              class={`hf-card interactive ${on0 ? 'p1' : ''} ${on1 ? 'p2' : ''} ${owned ? '' : 'locked'}`}
              onClick={() => clickCell(cell)}
              style={{ '--accent': c.accent }}
            >
              <div class="hf-card__rays" aria-hidden="true" />
              {!owned ? <LockBadge class="hf-card__lock" /> : null}
              <Head character={c.id} class="hf-card__head" />
              <div class="hf-card__name t-display">{c.name.replace('THE ', '')}</div>
              {!owned ? (
                <PriceChip price={cat.priceCoins} rarity={cat.rarity} class="hf-card__price" />
              ) : null}
              {on0 ? <span class="hf-card__cursor p1 t-display">1P</span> : null}
              {on1 ? <span class="hf-card__cursor p2 t-display">{p2Label}</span> : null}
            </button>
          );
        })}
        {PORTAL ? null : (
          <button
            role="option"
            aria-label="Get coins"
            aria-selected={
              (cursor[0] === GET_COINS_CELL && (phase === 'pick' || local || !!picked[0])) ||
              (cursor[1] === GET_COINS_CELL && (local || phase !== 'pick'))
            }
            class={`hf-card hf-card--coins interactive ${
              cursor[0] === GET_COINS_CELL && (phase === 'pick' || local || !!picked[0]) ? 'p1' : ''
            } ${cursor[1] === GET_COINS_CELL && (local || phase !== 'pick') ? 'p2' : ''}`}
            onClick={openStore}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" class="hf-card__coins-icon">
              <circle cx="12" cy="9" r="7" fill="#ffd23f" stroke="currentColor" stroke-width="1.5" />
              <path
                d="M6 13c-2 1-3 2.3-3 3.6C3 19 7 21 12 21s9-2 9-4.4c0-1.3-1-2.6-3-3.6"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
            <div class="hf-card__name t-display">GET COINS</div>
          </button>
        )}
        <button
          class={`hf-random interactive ${cursor[0] === RANDOM_CELL && !picked[0] ? 'p1' : ''} ${
            cursor[1] === RANDOM_CELL && (local || phase === 'cpu') ? 'p2' : ''
          }`}
          onClick={() => clickCell(RANDOM_CELL)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" class="hf-random__dice">
            <rect x="3" y="3" width="18" height="18" rx="4" fill="currentColor" />
            <circle cx="8" cy="8" r="1.7" fill="#ffd23f" />
            <circle cx="16" cy="16" r="1.7" fill="#ffd23f" />
            <circle cx="12" cy="12" r="1.7" fill="#ffd23f" />
            <circle cx="16" cy="8" r="1.7" fill="#ffd23f" />
            <circle cx="8" cy="16" r="1.7" fill="#ffd23f" />
          </svg>
          <span class="t-display">RANDOM HEAD</span>
        </button>
        {!local ? (
          <div class={`hf-difficulty ${phase === 'difficulty' ? 'active' : ''}`} aria-live="polite">
            <div class="hf-difficulty__label t-broadcast">CPU</div>
            <div class="hf-difficulty__opts" role="radiogroup" aria-label="CPU difficulty">
              {DIFFICULTIES.map((v) => (
                <button
                  key={v}
                  role="radio"
                  aria-checked={v === difficulty}
                  class={`hf-difficulty__opt t-broadcast interactive ${v === difficulty ? 'on' : ''}`}
                  onClick={() => {
                    setDifficulty(v);
                    if (phaseRef.current === 'difficulty')
                      finish([pickedRef.current[0] ?? 'rookie', pickedRef.current[1] ?? 'zorg'], v);
                  }}
                >
                  {DIFFICULTY_NAMES[v]}
                </button>
              ))}
            </div>
            <div class="hf-difficulty__blurb">{DIFFICULTY_BLURB[difficulty]}</div>
          </div>
        ) : null}
      </div>

      <Hints
        class="hf-select__hints"
        items={
          local
            ? localPickHints(input, inputStyle.value)
            : phase === 'difficulty'
              ? [
                  ['left|right', 'DIFFICULTY'],
                  ['confirm', 'PLAY'],
                  ['back', 'UNDO'],
                ]
              : [
                  ['up|down|left|right', 'MOVE'],
                  ['confirm', 'PICK'],
                  ['back', 'BACK'],
                ]
        }
      />
      <div class="hf-select__note">
        {d.specials ? 'Each head brings one signature special. ' : 'Specials are off for this match. '}
        {PORTAL ? null : 'Ranked online normalizes every head to identical stats and hitboxes.'}
      </div>
    </div>
  );
}

function Portrait({
  side,
  character,
  ready,
  tag,
  dim,
  flash,
}: {
  side: 0 | 1;
  character: CharacterId;
  ready: boolean;
  tag: string;
  dim: boolean;
  flash: number;
}) {
  const def = characterById(character);
  const sp = specialInfo(def.signature);
  const home = side === 0;
  return (
    <section
      class={`hf-portrait ${home ? 'home' : 'away'} ${ready ? 'ready' : ''} ${dim ? 'dim' : ''}`}
      aria-label={`${tag} ${def.name}`}
    >
      <div class="hf-portrait__spot" aria-hidden="true" />
      <div class="hf-portrait__rig" key={`${character}-${ready ? flash : 'x'}`}>
        <Head
          character={character}
          expression={ready ? 'celebrating' : 'determined'}
          flip={!home}
          class="hf-portrait__head"
        />
        <Glove side={home ? 'home' : 'away'} pose={ready ? 'fist' : 'ready'} class="hf-portrait__glove" />
      </div>
      <span class="hf-portrait__tag t-display">{tag}</span>
      {ready ? <span class="hf-portrait__stamp t-display">READY!</span> : null}
      <div class="hf-plate">
        <div class="hf-plate__name t-display">{def.name}</div>
        <div class="hf-plate__role">{def.role}</div>
        <div class="hf-plate__stats">
          {STAT_ROWS.map(([label, k]) => (
            <div class="hf-plate__stat" key={k}>
              <span class="t-broadcast">{label}</span>
              <Pips value={def.stats[k]} />
            </div>
          ))}
        </div>
        <div class="hf-plate__special" title={sp.blurb}>
          <span class="hf-plate__icon">
            <SpecialIcon special={def.signature} />
          </span>
          <span class="t-broadcast hf-plate__splabel">SPECIAL:</span>
          <span class="t-display hf-plate__spname">{sp.name}</span>
        </div>
        <div class="hf-plate__blurb">{sp.blurb}</div>
      </div>
    </section>
  );
}
